'use client';

import { ICartItem, ITotalData } from '@/api/types/cart';
import Button from '@/components/Button';
import CycleMsg from '@/components/CycleMsg';
import Image from '@/components/Image';
import { useStore } from '@/providers/app-store-provider';
import { formatPriceNumberWithSymbol } from '@/utils/price';
import { ServiceOutlined } from '@zeelool/icon';
import { useTranslations } from '@/i18n/use-translations';
import { useRouter } from 'next/navigation';
import { useMemo } from 'react';
import { useCartGaTracking } from '../hooks/useCartGaTracking';
import { useCartSensorsTracking } from '../hooks/useCartSensorsTracking';

interface CartRightProps {
  selectedItems: ICartItem[];
  totalData: ITotalData;
  checkoutLoading: boolean;
  onCheckout: () => void;
  maxSaving?: string; // 优惠券提示信息

  halfPriceData?: {
    status?: number;
    title?: string;
    content?: string;
    rule?: {
      rule_flag?: number;
      discount_rate?: string;
      discount_text?: string;
      threshold_price?: number;
      restrict_single?: number;
    };
  };
}

export function CartRight({
  selectedItems,
  totalData,
  checkoutLoading,
  onCheckout,
  maxSaving,
  halfPriceData,
}: CartRightProps) {
  const t = useTranslations('cart.cartRight');
  const router = useRouter();
  const { currency } = useStore((state) => state);

  // 神策埋点相关Hook
  const { trackCartFunctionBtnClick } = useCartSensorsTracking();

  // GA埋点相关Hook
  const { trackContinueShopping } = useCartGaTracking();

  const totalItems = selectedItems.reduce((sum, item) => sum + (item.qty || 0), 0);
  const hasDiscount = totalData.discount && totalData.discount !== '0.00';
  // 计算按钮是否应该显示为禁用状态
  const shouldShowDisabledStyle = selectedItems.length === 0; // 只有没选商品时显示禁用样式，但仍可点击
  // 计算小计价格（去除逗号）
  const subtotalNoComma = useMemo(() => {
    return String(totalData.subtotal || '0.00').replace(/,/g, '');
  }, [totalData.subtotal]);

  const onContinueShopping = () => {
    // 🔍 发送神策埋点 - 购物车功能按钮点击
    trackCartFunctionBtnClick('Continue shopping item');

    // 🔍 发送GA埋点 - continue_shopping
    trackContinueShopping();

    router.push('/');
  };

  // PC端渲染
  return (
    <div className="sticky">
      <div className="pt-[32px] px-[24px] pb-[24px] bg-[#ffffff]">
        {/* 订单信息 */}
        <div className="mb-[20px]">
          <div className="flex justify-between items-center mb-[20px]">
            <h2 className="text-[20px] font-bs">{t('orderSummary')}</h2>
            <span
              className="text-[16px]"
              dangerouslySetInnerHTML={{
                __html: `<span class="notranslate">${totalItems}</span> ${totalItems > 1 ? t('items') : t('item')}`,
              }}
            />
          </div>

          <div className="flex justify-between text-[16px] mb-[20px]">
            <span>{t('total')}</span>
            <span className="notranslate">
              {formatPriceNumberWithSymbol(totalData.total || '0.00', currency)}
            </span>
          </div>

          {hasDiscount && (
            <div className="flex justify-between text-[16px] mb-[20px]">
              <span>
                {halfPriceData?.rule?.discount_text || 'Discount'}{' '}
                {halfPriceData?.rule?.discount_rate || ''}% {t('off')}
              </span>
              <span className="notranslate">
                -{formatPriceNumberWithSymbol(totalData.discount, currency)}
              </span>
            </div>
          )}

          <div className="border-t flex justify-between items-center pt-[20px]">
            <span className="text-[16px] font-bs">{t('subtotal')}</span>
            <span className="text-[24px] font-bb notranslate">
              {formatPriceNumberWithSymbol(totalData.subtotal || '0.00', currency)}
            </span>
          </div>
        </div>

        {/* 优惠券提示 */}
        {selectedItems.length > 0 && maxSaving && (
          <div className="bg-[#e9ffbc] py-[6px] px-[10px] mb-[20px] flex items-center min-h-[44px]">
            <Image
              src="/cart/sale.png"
              alt="max save"
              width={20}
              height={20}
              className="w-[20px] h-[20px] mr-[8px]"
            />
            <span className="text-[14px]" dangerouslySetInnerHTML={{ __html: maxSaving }} />
          </div>
        )}

        {/* 结账按钮 */}
        <div className="relative mt-[32px]">
          <Button
            variant="contained"
            onClick={onCheckout}
            disabled={checkoutLoading}
            loading={checkoutLoading}
            className={`w-full !rounded-[2px] !text-[20px] !font-bb !h-[58px]`}
            sx={{
              '&:disabled': {
                color: '#ffffff',
              },
              '&.Mui-disabled': {
                color: '#ffffff',
              },
              // 没有选中商品时的禁用样式（但仍可点击）
              ...(shouldShowDisabledStyle &&
                !checkoutLoading && {
                  opacity: 0.2,
                  cursor: 'not-allowed',
                }),
            }}
          >
            {t('proceedToCheckout')}
          </Button>
          {/* 赠品图标 - 当 is_satisfied === 1 时显示 */}
          {totalData.show_gift?.is_satisfied === 1 && totalData.show_gift?.gift_image && (
            <div className="absolute top-[-31px] right-[16px] z-10">
              <div className="relative p-[5px] rounded-[5px] bg-[#baacff]">
                <Image
                  src={totalData.show_gift.gift_image}
                  alt=""
                  width={40}
                  height={40}
                  className="block rounded-[5px] w-[40px] h-[40px] object-cover"
                />
                {/* 三角形箭头 */}
                <div className="absolute bottom-[-6px] left-1/2 -translate-x-1/2 w-0 h-0 border-l-[8px] border-r-[8px] border-t-[8px] border-l-transparent border-r-transparent border-t-[#baacff]" />
              </div>
            </div>
          )}
          {/* 满赠横幅 - 当 is_satisfied === 2 时显示 */}
          {selectedItems.length > 0 &&
            totalData.show_gift?.is_satisfied === 2 &&
            totalData.show_gift?.copy_writing &&
            totalData.show_gift?.gift_image && (
              <div className="absolute top-[-31px] right-[-24px] z-10 bg-[rgba(40,40,40,0.9)] text-white flex items-center p-[5px] rounded-[5px]">
                <Image
                  src={totalData.show_gift.gift_image}
                  alt=""
                  width={40}
                  height={40}
                  className="block rounded-[5px] mr-[8px] w-[40px] h-[40px] object-cover"
                />
                <span
                  className="text-[14px] leading-[17px] mr-[10px] line-clamp-2"
                  style={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                  dangerouslySetInnerHTML={{
                    __html: totalData.show_gift.copy_writing,
                  }}
                />
                {/* 三角形箭头 */}
                <div className="absolute bottom-[-6px] left-[18px] w-0 h-0 border-l-[8px] border-r-[8px] border-t-[8px] border-l-transparent border-r-transparent border-t-[rgba(40,40,40,0.9)]" />
              </div>
            )}
        </div>

        {/* 分期付款信息 */}
        {selectedItems.length > 0 && Number(subtotalNoComma) > 0 && (
          <div className="mt-[10px]">
            <CycleMsg price={subtotalNoComma} />
          </div>
        )}

        {/* Continue Shopping */}
        <p className="text-center text-[16px] mt-[32px]">
          <span onClick={onContinueShopping} className="underline cursor-pointer">
            {t('continueShopping')}
          </span>
        </p>
      </div>

      {/* 质量保证信息 */}
      <div className="mt-[16px] h-[160px] border-[#dddddd] border-[4px] relative">
        <div className="flex items-center justify-center h-full">
          <div className="w-[59px] h-[59px] flex items-center justify-center mr-[25px] rounded-[50%] border-[#463ae8] border-[1px]">
            <ServiceOutlined className="text-[30px] text-primary-purple" />
          </div>
          <div className="w-[70%]">
            <div className="font-bs text-[14px] leading-[30px]">{t('qualityGuarantee')}</div>
            <p className="text-[14px] leading-[24px]">{t('qualityGuaranteeDesc')}</p>
            <Image
              src="/cart/zeelool.png"
              alt="Zeelool Eyeglasses"
              width={90}
              height={34}
              className="w-[90px] absolute bottom-[40px] right-[60px]"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
